'use client';

import React from "react";
import Image from 'next/image';

import { ChevronLeftIcon, ChevronRightIcon } from "../icons";

// Updated interface to match Prisma schema structure
export interface CarouselItemProps {
  carousel: {
    id: string;
    slug: string;
    destination?: string;
    type: string;
    items: Array<{
      id: string;
      imageUrl: string;
      sortOrder: number;
      isActive: boolean;
      isMobileOnly: boolean;
      translations: Array<{
        id: string;
        lang: string;
        caption?: string;
        link?: string;
        header?: string;
        subheader?: string;
        fullDescription?: string;
        buttonText1?: string;
        buttonLink1?: string;
        buttonText2?: string;
        buttonLink2?: string;
      }>;
    }>;
  };
  dictionary: any;
  lang: string;
}

const Carousel = ({ carousel, lang }: CarouselItemProps) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  const activeItems = carousel.items.filter((item) => {
    if (!item.isActive) {
      return false;
    }

    if (typeof window === 'undefined') {
      return true;
    }

    if (item.isMobileOnly) {
      return isMobile;
    }

    return isMobile || !item.isMobileOnly;
  });

  const getMediaType = (url: string) => {
    if (!url) {
      return 'unknown';
    }

    const normalized = url.toLowerCase();

    if (normalized.includes('youtube.com') || normalized.includes('youtu.be')) {
      return 'youtube';
    }

    if (normalized.endsWith('.mp4') || normalized.endsWith('.webm') || normalized.endsWith('.ogg') || normalized.endsWith('.mov')) {
      return 'video';
    }

    return 'image';
  };

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) {
      return null;
    }

    const idMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
    if (idMatch && idMatch[1]) {
      return `https://www.youtube.com/embed/${idMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${idMatch[1]}`;
    }

    return null;
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % activeItems.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + activeItems.length) % activeItems.length);

  // Get translation for current language, fallback to first available if current lang not found
  const getTranslation = (item: typeof activeItems[0]) => {
    const translation = item.translations.find(t => t.lang === lang);
    return translation || item.translations[0] || null;
  };

  if (activeItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-[70vh] bg-gray-100 dark:bg-gray-800">
        <p className="text-gray-500 dark:text-gray-400">No active slides available</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      <div
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {activeItems.map((item, index) => {
          const translation = getTranslation(item);
          const mediaType = getMediaType(item.imageUrl);
          const youTubeEmbedUrl = mediaType === 'youtube' ? getYouTubeEmbedUrl(item.imageUrl) : null;
          return (
            <div key={item.id} className="w-full flex-shrink-0">
              <div className="relative h-[70vh] md:h-[70vh] lg:h-[70vh]">
                {mediaType === 'image' && (
                  <Image
                    src={item.imageUrl}
                    alt={translation?.caption || translation?.header || 'Carousel image'}
                    fill
                    sizes="100vw"
                    className="object-cover"
                    quality={60}
                    priority={index === 0}
                  />
                )}
                {mediaType === 'video' && (
                  <video
                    className="w-full h-full object-cover"
                    src={item.imageUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls={false}
                    preload="metadata"
                  />
                )}
                {mediaType === 'youtube' && youTubeEmbedUrl && (
                  <iframe
                    className="w-full h-full object-cover"
                    src={youTubeEmbedUrl}
                    title={translation?.header || translation?.caption || 'Carousel video'}
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                )}
                {mediaType === 'youtube' && !youTubeEmbedUrl && (
                  <div className="flex h-full w-full items-center justify-center bg-black text-sm text-white">
                    Unable to load video content.
                  </div>
                )}
                {mediaType === 'unknown' && (
                  <div className="flex h-full w-full items-center justify-center bg-gray-200 text-gray-600"><span>Unsupported media format.</span></div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)] dark:drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
                  
                  <div className="flex-1 flex items-center justify-center min-h-0 mt-28">
                    <div className="text-center text-white p-4 md:p-8 max-w-6xl not-prose">
                      {translation?.header && (
                        <h1 className="text-balance text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight text-white">
                          {translation.header}
                        </h1>
                      )}

                      {translation?.subheader && (
                        <h2 className="mt-4 mb-4 text-balance text-lg sm:text-xl md:text-2xl lg:text-3xl tracking-tight text-white">
                          {translation.subheader || translation.subheader}
                        </h2>
                      )}

                      {translation?.fullDescription && (
                        <h3 className="mt-8 text-pretty text-sm sm:text-base md:text-lg lg:text-2xl text-white">
                          {translation.fullDescription}
                        </h3>
                      )}
                    </div>
                  </div>

                  {(translation?.buttonText1 || translation?.buttonText2) && (
                    <div className="flex items-center justify-center gap-x-6 pb-16 md:pb-20 flex-shrink-0">
                      {translation.buttonText1 && translation.buttonLink1 && (
                        <a
                          href={translation.buttonLink1}
                          className="bg-gray-800 border text-sm text-white shadow-sm hover:bg-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200 rounded-full px-8 py-2 transition-colors duration-200"
                        >
                          {translation.buttonText1}
                        </a>
                      )}
                      {translation.buttonText2 && translation.buttonLink2 && (
                        <a
                          href={translation.buttonLink2}
                          className="text-sm/6 text-white"
                        >
                          {translation.buttonText2} <span aria-hidden="true">→</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation arrows */}
      <button
        aria-label="Previous slide"
        title="Previous slide"
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 rounded-full p-2 hover:bg-opacity-75 transition duration-300"
      >
        <ChevronLeftIcon className="w-6 h-6 text-black" />
      </button>
      <button
        aria-label="Next slide"
        title="Next slide"
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 rounded-full p-2 hover:bg-opacity-75 transition duration-300"
      >
        <ChevronRightIcon className="w-6 h-6 text-black" />
      </button>

      {/* Dots navigation */}
      <div className="absolute bottom-4 left-0 right-0">
        <div className="flex items-center justify-center gap-1">
          {activeItems.map((_, i) => (
            <button
              aria-label={`Go to slide ${i + 1}`}
              aria-current={currentSlide === i ? 'true' : undefined}
              title={`Go to slide ${i + 1}`}
              key={i}
              className="w-11 h-11 inline-flex items-center justify-center rounded-full"
              onClick={() => setCurrentSlide(i)}
            >
              <span
                className={`
                  w-3 h-3 rounded-full transition-all duration-300
                  ${currentSlide === i ? 'bg-white scale-110' : 'bg-white bg-opacity-50'}
                `}
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Carousel;